<template>
  <minimized-call-window
    v-if="inCall && (!isAnswered || isMinimised)"
    :is-minimised="isMinimised"
    :is-admin-call="isAdminCall"
    @maximise="maximiseCall"
  />
  
  <active-call-page
    v-if="inCall && isAnswered"
    v-show="!isMinimised"
    @minimise="minimiseCall"
  />
  
  <audio ref="remoteAudio" autoplay="autoplay" id="remote-stream-audio"></audio>
</template>

<script>
import { computed, watch, onMounted, onUnmounted, ref } from 'vue';
import { useStore } from 'vuex';
import { useCallManager } from '@/composables/useCallManager';
import ActiveCallPage from '@/pages/call/ActiveCallPage.vue';
import MinimizedCallWindow from './MinimizedCallWindow.vue';
import WebRTCService from '@/services/WebRTCService';

export default {
  name: 'WebPhone',
  components: {
    ActiveCallPage,
    MinimizedCallWindow
  },
  props: {
    opened: {
      type: Boolean,
      default: false
    }
  },
  setup() {
    const store = useStore();
    const {
      // State
      inSession,
      phoneConnected,
      newCall,
      isMinimised,
      isAdminCall,
      
      // Computed
      sessionPeer,
      showDial,
      enableDial,
      
      // Methods
      initPhone,
      dialCall,
      answerCall,
      hangupCall,
      holdCall,
      muteCall,
      minimiseCall,
      maximiseCall,
      cleanup,
      forceReset,
      logoutCleanup, // Add logout cleanup method
    } = useCallManager();

    // Connection health monitoring
    const healthCheckInterval = ref(null);
    const lastHealthCheck = ref(Date.now());

    // Check connection health every 30 seconds
    const startHealthMonitoring = () => {
      stopHealthMonitoring();
      
      healthCheckInterval.value = setInterval(() => {
        checkConnectionHealth();
      }, 10000); // Check every 10 seconds instead of 30
    };

    const stopHealthMonitoring = () => {
      if (healthCheckInterval.value) {
        clearInterval(healthCheckInterval.value);
        healthCheckInterval.value = null;
      }
    };

    const checkConnectionHealth = () => {
      const now = Date.now();
      lastHealthCheck.value = now;
      
      // Only check if we're authenticated and have a domain
      if (!store.getters.isAuthenticated || !store.getters.domain) {
        return;
      }
      
      const isConnected = WebRTCService.isConnected();
      const isRegistered = WebRTCService.isRegistered();
      
      console.log('Connection health check:', { isConnected, isRegistered, phoneConnected: phoneConnected.value });
      
      // If WebRTC is not connected, try to reinitialize immediately
      if (!isConnected) {
        console.log('WebRTC not connected - attempting immediate reinitialization');
        initPhone();
        return;
      }
      
      // If connected but not registered, try to register immediately
      if (isConnected && !isRegistered) {
        console.log('WebRTC connected but not registered - attempting immediate registration');
        if (WebRTCService.coolPhone) {
          WebRTCService.coolPhone.register();
        }
        return;
      }
      
      // Additional check: if phone shows connected but WebRTC says not connected
      if (phoneConnected.value && !isConnected) {
        console.log('State mismatch detected - forcing reconnection');
        phoneConnected.value = false;
        store.commit('setIsConnected', false);
        initPhone();
      }
    };

    // Computed properties from store
    const currentLocal = computed(() => {
      return store.getters.currentLocal || 'en';
    });

    const inCall = computed(() => store.getters.inCall);
    const isAnswered = computed(() => store.getters.isAnswered);
    const answerRequest = computed(() => store.getters.answerRequest);
    const holdRequest = computed(() => store.getters.holdRequest);
    const muteRequest = computed(() => store.getters.muteRequest);
    const hangupRequest = computed(() => store.getters.hangupRequest);
    const status = computed(() => store.getters.status);
    const inAcw = computed(() => store.getters.inAcw);
    const numberToCall = computed(() => store.getters.numberToCall);

    // Watchers
    watch(() => store.getters.domain, async (newDomain) => {
      if (newDomain) {
        await initPhone();
      }
    });

    watch(answerRequest, () => {
      console.log('Answer request received');
      answerCall();
    });

    watch(holdRequest, () => {
      holdCall();
    });

    watch(muteRequest, () => {
      muteCall();
    });

    watch(hangupRequest, () => {
      console.log('Hangup request received');
      hangupCall();
      isMinimised.value = false;
    });

    watch(status, (newValue) => {
      console.log('Status changed to:', newValue);
      if (newValue !== 4) {
        // Only reinitialize if not connected and not already connecting
        if (store.getters.domain && !phoneConnected.value && !WebRTCService.isConnected() && !WebRTCService.isWebSocketConnected()) {
          console.log('Reinitializing phone after status change');
          initPhone();
        }
      } else {
        console.log('Cleaning up due to status change to 4');
        cleanup();
      }
    });

    watch(inAcw, (val) => {
      // Handle ACW (After Call Work) state changes
      if (val && store.getters.phoneConnected) {
        // Stop phone when in ACW
        store.dispatch('SetPhoneState', { stop: true });
      } else if (!val && !store.getters.phoneConnected) {
        // Start phone when not in ACW
        store.dispatch('SetPhoneState', { start: true });
      }
    });

    watch(numberToCall, async (val) => {
      if (val && val !== '') {
        await dialCall(val);
        store.commit('setNumberToCall', '');
      }
    });

    // Watch for WebRTC health check requests from app WebSocket reconnections
    watch(() => store.getters.webRTCNeedsCheck, (needsCheck) => {
      if (needsCheck) {
        console.log('WebRTC health check requested by app WebSocket reconnection');
        checkConnectionHealth();
        // Reset the flag
        store.commit('setWebRTCNeedsCheck', false);
      }
    });

    // Watch for authentication changes to handle logout cleanup
    watch(() => store.getters.isAuthenticated, (isAuthenticated, wasAuthenticated) => {
      if (wasAuthenticated && !isAuthenticated) {
        console.log('User logged out - performing complete cleanup');
        
        // Stop health monitoring
        stopHealthMonitoring();
        
        // Perform logout cleanup
        logoutCleanup();
      }
    });

    watch(() => store.state.sessionLogOff, (newValue) => {
      console.log('Session logoff state changed:', newValue);
      if (newValue) {
        console.log('Performing cleanup due to session logoff');
        
        // Stop health monitoring
        stopHealthMonitoring();
        
        // Use logout cleanup for complete cleanup
        logoutCleanup();
      } else {
        if (store.getters.domain && !phoneConnected.value) {
          console.log('Reinitializing phone after session logoff reset');
          initPhone();
          startHealthMonitoring(); // Restart health monitoring
        }
      }
    });

    // Lifecycle hooks
    onMounted(async () => {
      console.log('WebPhone component mounted, initializing phone...');
      await initPhone();
      startHealthMonitoring(); // Start health monitoring on mount
    });

    onUnmounted(() => {
      console.log('WebPhone component unmounting, performing cleanup...');
      forceReset(); // Use force reset for complete cleanup on unmount
      stopHealthMonitoring(); // Stop health monitoring on unmount
    });

    return {
      // State
      inSession,
      phoneConnected,
      newCall,
      isMinimised,
      isAdminCall,
      
      // Computed
      sessionPeer,
      showDial,
      enableDial,
      currentLocal,
      inCall,
      isAnswered,

      // Methods
      dialCall,
      minimiseCall,
      maximiseCall,
      logoutCleanup, // Add logout cleanup method
      
      // Health monitoring
      startHealthMonitoring,
      stopHealthMonitoring,
      checkConnectionHealth,
    };
  }
};
</script>

<style scoped>
/* Component-specific styles can be added here if needed */
</style>
